/**
 * @file 学びメモの手書き (メモの添付の1種) を読み、書き足しを間引いて保存する hook である。
 *
 * @what: 選んだ手書きの画像の線を読み、線を足したら2.5秒後に PNG と線を同じパスへ上書きする。
 *   まだ手書きが無いときは、最初の保存で新しい画像として上げ、親に知らせる (本文に参照を差し込む)。
 *   メモや手書きを切り替えるときは、切り替える前のものへ待たずに保存する。送信中に足した線は
 *   送信が終わってから改めて保存する (二重に作らない)。
 * @why: 手書きはメモの一部で、テキストと同じ間引きで自動保存する (代表の判断、2026-09-09)。
 *   同じパスに上書きすれば本文の参照は変わらず、AI 採点はいつも最新の絵を読む。
 * @failure: 読みの失敗は空の文書として扱う。保存の失敗は状態に出し、線は消さない。
 *   画像の枚数が上限なら新しい手書きは作らず、その旨を状態に出す。
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { StudyNoteImage } from "../api/notes";
import { exportHandwritingPng } from "../lib/handwritingExport";
import { evictPageImage } from "../lib/pageImageCache";
import {
  fetchStudyNoteDrawing,
  saveStudyNoteDrawing,
  uploadStudyNoteDrawing,
} from "../lib/studyLogImage";
import {
  emptyHandwriting,
  parseHandwriting,
  serializeHandwriting,
  type HandwritingDoc,
} from "../model/handwriting";
import { useInkHistory } from "./useInkHistory";

/** 線を足してから保存するまでの待ち (ms)。テキストの自動保存と同じにする。 */
const SAVE_DELAY_MS = 2500;
/** 送信中に足した線を、送信が終わったあとに保存し直すまでの待ち (ms)。 */
const RETRY_DELAY_MS = 500;

export type NoteInkStatus = "idle" | "loading" | "saving" | "saved" | "error" | "full";

export function useNoteInk({
  familyId,
  noteId,
  image,
  active,
  canCreate,
  onCreate,
  onSaved,
}: {
  familyId: string;
  /** 開いているメモ (無ければ書けない)。 */
  noteId: string | null;
  /** 選んでいる手書きの画像 (null = まだ無い。最初の保存で作る)。 */
  image: StudyNoteImage | null;
  /** 手書きのペインが見えているか。見えていないときは線を読まない。 */
  active: boolean;
  /** 新しい画像を足せるか (メモの画像の上限)。 */
  canCreate: boolean;
  /** 新しい手書きを作ったときに呼ぶ (本文に参照を差し込み、images に足す)。 */
  onCreate: (image: StudyNoteImage) => void;
  /** 既にある手書きを上書きしたときに呼ぶ (表示している絵を取り直す)。 */
  onSaved: (image: StudyNoteImage) => void;
}) {
  const ink = useInkHistory();
  const [status, setStatus] = useState<NoteInkStatus>("idle");
  const key = noteId !== null && active ? `${noteId}/${image?.id ?? ""}` : null;
  // 直前に読んでいたもの (切り替えのときの保存先)。effect の中でだけ更新する。
  const lastRef = useRef<{ key: string | null; image: StudyNoteImage | null; noteId: string | null }>({
    key: null,
    image: null,
    noteId: null,
  });
  const imageRef = useRef(image);
  const noteRef = useRef(noteId);
  const canCreateRef = useRef(canCreate);
  const docRef = useRef(ink.doc);
  const changedRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);
  // 作った直後は読み直さない (上げている間に足した線を失わないため)。
  const skipLoadRef = useRef<string | null>(null);
  const onCreateRef = useRef(onCreate);
  const onSavedRef = useRef(onSaved);
  onCreateRef.current = onCreate;
  onSavedRef.current = onSaved;
  imageRef.current = image;
  noteRef.current = noteId;
  canCreateRef.current = canCreate;
  docRef.current = ink.doc;
  changedRef.current = ink.changed;

  const persist = useCallback(
    async (doc: HandwritingDoc, target: StudyNoteImage | null, note: string | null) => {
      if (note === null) return;
      // まだ無い手書きに線が無ければ作らない。既にあるものは空でも上書きする (全消しを残す)。
      if (target === null && doc.strokes.length === 0) return;
      if (target === null && !canCreateRef.current) {
        setStatus("full");
        return;
      }
      inFlightRef.current = true;
      setStatus("saving");
      try {
        const png = await exportHandwritingPng(doc);
        const json = serializeHandwriting(doc);
        if (target) {
          await saveStudyNoteDrawing(png, json, target.path);
          evictPageImage(target.path);
          onSavedRef.current(target);
        } else {
          const created = await uploadStudyNoteDrawing(png, json, familyId, note);
          skipLoadRef.current = created.id;
          onCreateRef.current(created);
        }
        setStatus("saved");
      } catch (err) {
        console.error("[study] failed to save note ink", err);
        setStatus("error");
      } finally {
        inFlightRef.current = false;
      }
    },
    [familyId],
  );

  // 間引きの本体である。送信中なら少し待ってからもう一度試す (二重に作らない)。
  const schedule = useCallback(
    (delay: number) => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        if (inFlightRef.current) {
          schedule(RETRY_DELAY_MS);
          return;
        }
        ink.markSaved();
        void persist(docRef.current, imageRef.current, noteRef.current);
      }, delay);
    },
    [ink, persist],
  );

  // メモや手書きが変わったら、前の書きかけを待たずに保存し、新しいものを読む。
  useEffect(() => {
    const prev = lastRef.current;
    if (prev.key !== null && prev.key !== key && changedRef.current) {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      changedRef.current = false;
      void persist(docRef.current, prev.image, prev.noteId);
    }
    lastRef.current = { key, image, noteId };
    if (image && skipLoadRef.current === image.id) {
      // 作った直後で、線は手元にある。送信中に足した分は間引きがそのまま保存する。
      skipLoadRef.current = null;
      return;
    }
    ink.reset(emptyHandwriting());
    if (key === null || !image) {
      setStatus("idle");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    fetchStudyNoteDrawing(image.path)
      .then((raw) => {
        if (cancelled) return;
        ink.reset(parseHandwriting(raw) ?? emptyHandwriting());
        setStatus("idle");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error("[study] failed to load note ink", err);
        ink.reset(emptyHandwriting());
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // 線が変わったら間引いて保存する。
  useEffect(() => {
    if (!ink.changed || key === null) return;
    schedule(SAVE_DELAY_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ink.doc, ink.changed, key]);

  // unmount では待たずに保存する。
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      if (changedRef.current) void persist(docRef.current, imageRef.current, noteRef.current);
    };
  }, [persist]);

  return { ...ink, status };
}
